import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { clientsApi } from "@/api/clients";
import { metricsApi } from "@/api/metrics";
import { gasPurchasesApi, parametersApi } from "@/api/parameters";
import { PeriodNav } from "@/components/PeriodNav";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { type Period, calendarRangeFor, fmt as fmtCanonical, num as numCanonical, todayIso } from "@/lib/format";

const num = (v: number | string | null | undefined): number => numCanonical(v) ?? 0;
const fmt = (n: number | null, digits = 0): string => fmtCanonical(n, digits);

export function StatisticsPage() {
  const { t } = useTranslation();
  const [end, setEnd] = useState(todayIso());
  const [period, setPeriod] = useState<Period>("month");

  const { from, to } = useMemo(() => calendarRangeFor(end, period), [end, period]);

  const daily = useQuery({
    queryKey: ["metrics-daily", from, to],
    queryFn: () => metricsApi.daily(from, to),
  });

  const kgu = useQuery({
    queryKey: ["kgu-params", end],
    queryFn: () => parametersApi.getKguParams(end),
  });

  // Передзакуплений газ (FR-PARAMS-05): витрачено за період + залишок партій.
  const gasBalance = useQuery({
    queryKey: ["gas-balance", from, to],
    queryFn: () => gasPurchasesApi.balance(from, to),
  });

  const clientsQ = useQuery({
    queryKey: ["clients", { includeInactive: false }],
    queryFn: () => clientsApi.list(false),
  });
  const fixedClients = useMemo(
    () => (clientsQ.data ?? []).filter((c) => c.tariff_type === "fixed" && c.is_active),
    [clientsQ.data],
  );

  const stats = useMemo(() => {
    const rows = daily.data ?? [];
    let totalKwh = 0;
    let totalProfit = 0;
    let totalRevenue = 0;
    let totalCost = 0;
    let totalHeat = 0;
    let alarmHours = 0;
    let hoursTotal = 0;
    let totalEkoFact = 0;
    let totalEkoPlan = 0;
    for (const r of rows) {
      const gen = num(r.fact_generation_kwh);
      totalKwh += gen;
      totalProfit += num(r.fact_profit_uah);
      totalRevenue += num(r.fact_revenue_uah);
      totalCost += num(r.fact_revenue_uah) - num(r.fact_profit_uah);
      totalHeat += num(r.fact_heat_gcal);
      alarmHours += r.alarm_hours;
      hoursTotal += r.fact_hours;
      totalEkoFact += num(r.fact_volume_ekosfera);
      totalEkoPlan += num(r.plan_volume_ekosfera);
    }

    const dayCount = rows.length || 1;
    const avgPower = hoursTotal > 0 ? totalKwh / hoursTotal : 0;
    const pmax = num(kgu.data?.power_max_kw);
    const kium = pmax > 0 ? (avgPower / pmax) * 100 : 0;
    // Off-години/дні: генерація 0 → грн/МВт·год не визначені → null (UI покаже «—»).
    const profitPerMwh = totalKwh > 0 ? totalProfit / (totalKwh / 1000) : null;
    const revenuePerMwh = totalKwh > 0 ? totalRevenue / (totalKwh / 1000) : null;
    const avgCostPerKwh = totalKwh > 0 ? totalCost / totalKwh : null;
    // Середнє споживання ЕкоСфера = Σ факт-обсягу ЕкоСфера / Σ годин з фактом.
    const avgEkoPerHour = hoursTotal > 0 ? totalEkoFact / hoursTotal : 0;
    // Середнє відхилення = (Σ факт − Σ план) / Σ план × 100% (signed).
    // > 0 — ЕкоСфера споживала більше плану; < 0 — менше.
    const avgEkoDeviationPct =
      totalEkoPlan > 0 ? ((totalEkoFact - totalEkoPlan) / totalEkoPlan) * 100 : 0;

    return {
      totalKwh,
      totalProfit,
      totalHeat,
      avgPower,
      kium,
      profitPerMwh,
      revenuePerMwh,
      avgCostPerKwh,
      avgEkoPerHour,
      avgEkoDeviationPct,
      alarmHours,
      dayCount,
    };
  }, [daily.data, kgu.data]);

  const chartData = useMemo(
    () =>
      (daily.data ?? []).map((r) => {
        const gen = num(r.fact_generation_kwh);
        const profit = num(r.fact_profit_uah);
        return {
          day: r.day.slice(5),
          avg_power_kw: r.fact_hours > 0 ? gen / r.fact_hours : 0,
          profit_per_mwh: gen !== null && gen > 0 && profit !== null ? profit / (gen / 1000) : null,
        };
      }),
    [daily.data],
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("statistics.title")}</CardTitle>
          <CardDescription>{t("statistics.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <PeriodNav
            period={period}
            refDate={end}
            onPeriodChange={setPeriod}
            onRefDateChange={setEnd}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={t("statistics.kium")} value={`${fmt(stats.kium, 1)}%`} />
        <Stat
          label={t("statistics.avg_power")}
          value={`${fmt(stats.avgPower, 0)} ${t("common.kw")}`}
        />
        <Stat label={t("statistics.profit_per_mwh")} value={fmt(stats.profitPerMwh, 0)} />
        <Stat label={t("statistics.revenue_per_mwh")} value={fmt(stats.revenuePerMwh, 0)} />
        <Stat
          label={t("statistics.total_kwh")}
          value={`${fmt(stats.totalKwh / 1000, 1)} ${t("statistics.unit_mwh")}`}
        />
        <Stat
          label={t("statistics.total_heat")}
          value={`${fmt(stats.totalHeat, 1)} ${t("statistics.unit_gcal")}`}
        />
        <Stat
          label={t("statistics.avg_cost_per_kwh")}
          value={`${fmt(stats.avgCostPerKwh, 2)} ${t("statistics.unit_uah_per_kwh")}`}
        />
        <Stat
          label={t("statistics.alarm_hours")}
          value={String(stats.alarmHours)}
          tone={stats.alarmHours > 0 ? "negative" : undefined}
        />
        <Stat
          label={t("statistics.avg_eko_per_hour")}
          value={`${fmt(stats.avgEkoPerHour, 0)} ${t("common.kw")}·${t("statistics.hour_short")}`}
        />
        <Stat
          label={t("statistics.gas_prepaid_used")}
          value={`${fmt(num(gasBalance.data?.used_m3), 0)} ${t("statistics.unit_m3")}`}
        />
        <Stat
          label={t("statistics.gas_prepaid_remaining")}
          value={`${fmt(num(gasBalance.data?.remaining_m3), 0)} ${t("statistics.unit_m3")}`}
        />
        <Stat
          label={t("statistics.avg_eko_deviation")}
          value={`${stats.avgEkoDeviationPct >= 0 ? "+" : ""}${fmt(stats.avgEkoDeviationPct, 1)}%`}
          tone={stats.avgEkoDeviationPct < 0 ? "negative" : undefined}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("statistics.chart_avg_power")}</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div className="text-sm text-slate-500">{t("home1.no_data")}</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis tickFormatter={(v) => fmt(v as number)} />
                  <Tooltip formatter={(v) => fmt(v as number)} />
                  <Line
                    type="monotone"
                    dataKey="avg_power_kw"
                    stroke="#2563eb"
                    dot={false}
                    name={t("statistics.avg_power")}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("statistics.chart_profit_per_mwh")}</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div className="text-sm text-slate-500">{t("home1.no_data")}</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis tickFormatter={(v) => fmt(v as number)} />
                  <Tooltip formatter={(v) => fmt(v as number)} />
                  <Line
                    type="monotone"
                    dataKey="profit_per_mwh"
                    stroke="#10b981"
                    dot={false}
                    name={t("statistics.profit_per_mwh")}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* План/факт обсягів кожного fixed-tariff клієнта (план — бар, факт — лінія) */}
      {fixedClients.map((c) => (
        <ClientPlanFactChart
          key={c.code}
          clientCode={c.code}
          clientName={c.name}
          rows={daily.data ?? []}
        />
      ))}
    </div>
  );
}

function ClientPlanFactChart({
  clientCode,
  clientName,
  rows,
}: {
  clientCode: string;
  clientName: string;
  rows: import("@/api/metrics").DailyAgg[];
}) {
  const { t } = useTranslation();
  const data = useMemo(
    () =>
      rows.map((r) => {
        const plan = num(r.plan_volumes_by_client?.[clientCode] ?? null);
        const fact = num(r.fact_volumes_by_client?.[clientCode] ?? null);
        return { day: r.day.slice(5), plan, fact };
      }),
    [rows, clientCode],
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t("statistics.chart_client_plan_fact", { client: clientName })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="text-sm text-slate-500">{t("home1.no_data")}</div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis tickFormatter={(v) => fmt(v as number)} />
                <Tooltip formatter={(v) => fmt(v as number)} />
                <Legend />
                <Bar
                  dataKey="plan"
                  fill="#cbd5e1"
                  name={t("statistics.legend_plan")}
                />
                <Line
                  type="monotone"
                  dataKey="fact"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={false}
                  name={t("statistics.legend_fact")}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "negative";
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div
          className={
            "text-xl font-mono font-semibold " + (tone === "negative" ? "text-red-700" : "")
          }
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
